import React, { Fragment, FunctionComponent, useEffect, useState } from "react";
import styles from "./ReportDownload.module.scss";
import ReportDownloadProps from "./ReportDownloadProps.interface";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import Paragraph from "components/Paragraph/Paragraph";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import Card from "./components/Card/Card";
import { useHistory } from "react-router-dom";
import useFilter from "blocks/hooks/useFilter";
import useSplitBackground from "blocks/hooks/useSplitBackground";

const ReportDownload: FunctionComponent<ReportDownloadProps> = (props): JSX.Element => {
    const history = useHistory();

    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const headlineCaseClass = props.settings.headlineCase ? styles[`case_${props.settings.headlineCase}`] : undefined;

    const [items, setItems] = useState(props.content.logos);
    const [filters, viewState] = useFilter(history.location.pathname);
    const [currentFilters, setCurrentFilters] = useState<any[]>([]);

    const hasOverflowClass = items && items.length >= 4 ? styles.has_overflow : undefined;

    // Clear filters
    const clearFilters = () => {
        setCurrentFilters([]);

        // Setting URL Params
        history.replace({
            search: undefined,
        });

        setItems(props.content.logos);
    };

    const noFiltersClass = !currentFilters || (currentFilters && currentFilters.length === 0) ? styles.is_active : undefined;

    // Handle tag click event
    const handleTagClick = (givenFilter: any) => {
        const filterId = givenFilter.id.toString();

        // Setting current filter
        if (typeof (filterId) !== "undefined" && filterId !== null) {
            const existingFilter = currentFilters.find(x => x === filterId);

            if (currentFilters && currentFilters.length > 0 && existingFilter?.[0]) {
                // Filter out the givenFilter from currentFilters
                setCurrentFilters(currentFilters.filter(x => x !== filterId));

                // Setting URL Params
                history.replace({
                    search: undefined,
                });
            }
            else {
                setCurrentFilters([...currentFilters, filterId]);

                // Setting URL Params
                history.replace({
                    pathname: location.pathname,
                    search: `?f=${givenFilter.id}`,
                    hash: location.hash
                });
            }
        }
    };

    // Update

    useEffect(() => {
        if (currentFilters?.[0]) {
            const itemsFiltered = props.content?.logos?.filter((item: any) => {
                // Iterate through every currentFilter and check
                // if every currentFilter is also in the item-filter list
                return currentFilters?.every((currentFilter: any) => {
                    return item.content?.filters.map((x: any) => x._id.toString()).includes(currentFilter);
                });
            });

            setItems(itemsFiltered);
        }
        else {
            setItems(props.content.logos);
        }
    }, [currentFilters]);

    // Init

    useEffect(() => {
        const query = new URLSearchParams(history.location.search);
        const filterId = query.get("f");

        // Setting current filter
        if (typeof (filterId) !== "undefined" && filterId !== null) {
            setCurrentFilters([filterId]);
        }
    }, []);

    return (
        <section className={combineClasses(styles.wrapper, ...wrapperClasses)} style={{ ...wrapperStyles, ...splitStyles }}>
            <div className={combineClasses(styles.container, ...splitClasses, inViewClasses)} ref={inViewElementRef}>

                {/* Headline */}
                {props.content.headline ? (
                    <DynamicHeading key={props.contentUdi} className={combineClasses(styles.headline, headlineCaseClass)} text={props.content.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight} />
                ) : null}

                {/* Text */}
                {props.content.bodyText ? (
                    <Paragraph className={styles.text} innerHTML={props.content.bodyText} />
                ) : null}

                {/* Filters */}
                {viewState === "LOADED" ? (
                    <div className={styles.tags}>
                        <button tabIndex={0} onClick={clearFilters} className={combineClasses(styles.tag, noFiltersClass)}>
                            <div>All</div>
                        </button>

                        {filters && filters?.map((filter: any) => {
                            const currentFilterClass = currentFilters.includes(filter.id.toString()) ? styles.is_active : undefined;

                            return (
                                <button key={filter.id} tabIndex={0} onClick={() => handleTagClick(filter)} className={combineClasses(styles.tag, currentFilterClass)}>
                                    {filter.label}
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                {/* Logos */}
                {items && items.length > 0 ? (
                    <div className={combineClasses(styles.list, hasOverflowClass)}>
                        {items.map((item: any, index: any) => {
                            return (
                                <div className={styles.item} key={index}>
                                    <Card key={item.contentUdi} {...item} />
                                </div>
                            );
                        })}
                    </div>
                ) : null}
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia key={props.contentUdi} {...props.settings.backgroundMedia[0]} />
            ) : null}

        </section>
    );
};

export default ReportDownload;